import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { 
  Box, 
  Typography, 
  Tab, 
  Tabs, 
  Button, 
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Switch,
  FormControlLabel,
  CircularProgress,
  IconButton,
  Snackbar,
  Alert,
  Checkbox,
  FormGroup,
  Chip,
  Tooltip
} from '@mui/material';
import DialogWrapper from '@/components/DialogWrapper';
import { Close as CloseIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { UserSettings, UserSettingsUpdate } from '@/api/userSettings';
import PermissionRequestsTab from './PermissionRequestsTab';
import PendingRequestsBadge from '@/components/PendingRequestsBadge';
import ModelsTab from './ModelsTab';
import { ModelsAPI, Model } from '@/api/models';
import AgentsTab from './AgentsTab';
import FeaturesTab from './FeaturesTab';
import SystemSettingsTab from './SystemSettingsTab';
import UsersTab from './UsersTab';
import DashboardTab from './DashboardTab';
import { useAllUserSettingsQuery } from '@/features/userSettings/hooks/useUserSettingsQuery';
import { useDeleteUserSettingsMutation, useUpdateUserSettingsMutation } from '@/features/userSettings/hooks/useUserSettingsMutations';
import { queryKeys } from '@/lib/queryKeys';
import { QueryErrorState, QueryStaleDataWarning } from '@/components/state';
import { useAppStore } from '@/store';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pl: 2, pt: 0, pb: 2, pr: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `admin-tab-${index}`,
    'aria-controls': `admin-tabpanel-${index}`,
  };
}

interface AdminPanelProps {
  userRole?: 'admin' | 'manager' | null;
  isAdmin?: boolean;
  currentUserId?: string;
}

export const AdminPanel: React.FC<AdminPanelProps> = ({ userRole, isAdmin, currentUserId }) => {
  const queryClient = useQueryClient();
  const [tabValue, setTabValue] = useState(0);
  const pendingPermissionRequestsCount = useAppStore((state) => state.pendingPermissionRequestsCount);
  const refreshPendingPermissionRequestsCount = useAppStore((state) => state.refreshPendingPermissionRequestsCount);
  const canReviewPermissionRequests = isAdmin || userRole === 'manager';
  //const [isAdmin, setIsAdmin] = useState<boolean>(false);
  // Define tabs based on role - managers don't see Models, Agents, Features, System Settings
  const allTabs = [
    { label: 'Dashboard', adminOnly: false },
    { label: 'Users', adminOnly: false },
    { label: 'Permission Requests', adminOnly: false },
    { label: 'Models', adminOnly: true },
    { label: 'Agents', adminOnly: true },
    { label: 'Features', adminOnly: true },
    { label: 'System Settings', adminOnly: true },
  ];
  const visibleTabs = allTabs.filter(tab => isAdmin || !tab.adminOnly);

  useEffect(() => {
    if (!canReviewPermissionRequests) {
      return;
    }

    void refreshPendingPermissionRequestsCount();
  }, [canReviewPermissionRequests, refreshPendingPermissionRequestsCount]);
  const {
    data: usersData,
    isPending: loading,
    isError: isUsersError,
    isRefetchError: isUsersRefetchError,
    error: usersQueryError,
  } = useAllUserSettingsQuery();
  const updateUserSettingsMutation = useUpdateUserSettingsMutation();
  const deleteUserSettingsMutation = useDeleteUserSettingsMutation();
  const users = usersData ?? [];
  const usersErrorMessage = isUsersError
    ? (usersQueryError instanceof Error ? usersQueryError.message : 'Failed to load users')
    : null;
  const hasCachedUsers = users.length > 0;
  const showUsersStaleWarning = Boolean(isUsersRefetchError && hasCachedUsers && usersErrorMessage);
  const blockingUsersError = hasCachedUsers ? null : usersErrorMessage;
  const [editingUser, setEditingUser] = useState<UserSettings | null>(null);
  const [bulkEditingUsers, setBulkEditingUsers] = useState<UserSettings[]>([]);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  
  // Available models for permission assignment
  const [availableModels, setAvailableModels] = useState<Model[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [selectedModelIds, setSelectedModelIds] = useState<string[]>([]);
  
  // Available agents for permission assignment
  const [availableAgents, setAvailableAgents] = useState<any[]>([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [selectedAgentIds, setSelectedAgentIds] = useState<string[]>([]);
  
  // Available features for permission assignment
  const [availableFeatures, setAvailableFeatures] = useState<any[]>([]);
  const [featuresLoading, setFeaturesLoading] = useState(false);
  const [selectedFeatureIds, setSelectedFeatureIds] = useState<string[]>([]);
  
  // Edited user state (legacy fields)
  // legacy fields removed - fresh DB schema
  
  // Edited user state (v3 fields)
  //const [role, setRole] = useState<string>('user');
  // permissions state handled via selected resource arrays
  const [businessUnit, setBusinessUnit] = useState<string>('Unassigned');
  const [managerId, setManagerId] = useState<string>('');
  const [managerName, setManagerName] = useState<string>('');
  const [isActive, setIsActive] = useState<boolean>(true);
  const [changeIsAdmin, setChangeIsAdmin] = useState<boolean>(editingUser?.is_admin || false)
  
  
  // New user creation handled outside Admin Panel

  const fetchUsers = async () => {
    try {
      await queryClient.invalidateQueries({ queryKey: queryKeys.userSettings.list() });
    } catch (error) {
      console.error('Error fetching users:', error);
      showSnackbar('Failed to fetch users', 'error');
    }
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleActionClick = async (user: UserSettings, editMode: boolean) => {
    setEditingUser(user);
    setEditMode(editMode);
    setChangeIsAdmin(user.is_admin ?? false)
    // V3 fields
    //setRole(user.role || 'user');
    setBusinessUnit(user.business_unit || 'Unassigned');
    setManagerId(user.manager_id || '');
    setManagerName((user as any).manager_name || '');
    setIsActive(user.is_active !== false);
    
    // Parse existing model permissions
    const modelPermissions = (user.permissions || [])
      .filter(p => p.startsWith('model:'))
      .map(p => p.replace('model:', ''));
    setSelectedModelIds(modelPermissions);
    
    // Parse existing agent permissions
    const agentPermissions = (user.permissions || [])
      .filter(p => p.startsWith('agent:'))
      .map(p => p.replace('agent:', ''));
    setSelectedAgentIds(agentPermissions);
    
    // Parse existing feature permissions
    const featurePermissions = (user.permissions || [])
      .filter(p => p.startsWith('feature:'))
      .map(p => p.replace('feature:', ''));
    setSelectedFeatureIds(featurePermissions);
    
    // Load available resources
    await Promise.all([
      fetchAvailableModels(),
      fetchAvailableAgents(),
      fetchAvailableFeatures()
    ]);
    
    setDetailsDialogOpen(true);
  };

  const handleBulkEditClick = async (selectedUsers: UserSettings[]) => {
    if (!selectedUsers.length) {
      showSnackbar('Please select at least one user', 'error');
      return;
    }

    setBulkEditingUsers(selectedUsers);

    const firstUser = selectedUsers[0];
    setChangeIsAdmin(firstUser.is_admin ?? false);
    setIsActive(firstUser.is_active !== false);

    const modelPermissions = (firstUser.permissions || [])
      .filter(p => p.startsWith('model:'))
      .map(p => p.replace('model:', ''));
    setSelectedModelIds(modelPermissions);

    const agentPermissions = (firstUser.permissions || [])
      .filter(p => p.startsWith('agent:'))
      .map(p => p.replace('agent:', ''));
    setSelectedAgentIds(agentPermissions);

    const featurePermissions = (firstUser.permissions || [])
      .filter(p => p.startsWith('feature:'))
      .map(p => p.replace('feature:', ''));
    setSelectedFeatureIds(featurePermissions);

    await Promise.all([
      fetchAvailableModels(),
      fetchAvailableAgents(),
      fetchAvailableFeatures()
    ]);

    setBulkDialogOpen(true);
  };
  
  const fetchAvailableModels = async () => {
    try {
      setModelsLoading(true);
      const modelsAPI = new ModelsAPI();
      const response = await modelsAPI.getModels({ per_page: 100 });

      // Normalize response shape - ModelsAPI may return array or object
      let modelsArray: Model[] = [];
      if (Array.isArray(response)) {
        modelsArray = response as unknown as Model[];
      } else if (response && Array.isArray((response as any).models)) {
        modelsArray = (response as any).models as Model[];
      } else if (response && Array.isArray((response as any).items)) {
        modelsArray = (response as any).items as Model[];
      } else if (response && Array.isArray((response as any).data)) {
        modelsArray = (response as any).data as Model[];
      } else {
        console.warn('Unexpected models response shape in AdminPanel, falling back to empty array', response);
        modelsArray = [];
      }

      setAvailableModels(modelsArray);
    } catch (error) {
      console.error('Error fetching models:', error);
      showSnackbar('Failed to load models', 'error');
    } finally {
      setModelsLoading(false);
    }
  };
  
  const fetchAvailableAgents = async () => {
    try {
      setAgentsLoading(true);
      const { fetchAgents } = await import('../../api/models');
      const agentsData = await fetchAgents();
      setAvailableAgents(agentsData);
    } catch (error) {
      console.error('Error fetching agents:', error);
      showSnackbar('Failed to load agents', 'error');
    } finally {
      setAgentsLoading(false);
    }
  };
  
  const fetchAvailableFeatures = async () => {
    try {
      setFeaturesLoading(true);
      const { fetchFeatures } = await import('../../api/models');
      const featuresData = await fetchFeatures();
      setAvailableFeatures(featuresData);
    } catch (error) {
      console.error('Error fetching features:', error);
      showSnackbar('Failed to load features', 'error');
    } finally {
      setFeaturesLoading(false);
    }
  };
  
  const handleModelPermissionToggle = (modelId: string) => {
    setSelectedModelIds(prev => {
      if (prev.includes(modelId)) {
        return prev.filter(id => id !== modelId);
      } else {
        return [...prev, modelId];
      }
    });
  };
  
  const handleAgentPermissionToggle = (agentId: string) => {
    setSelectedAgentIds(prev => {
      if (prev.includes(agentId)) {
        return prev.filter(id => id !== agentId);
      } else {
        return [...prev, agentId];
      }
    });
  };
  
  const handleFeaturePermissionToggle = (featureId: string) => {
    setSelectedFeatureIds(prev => {
      if (prev.includes(featureId)) {
        return prev.filter(id => id !== featureId);
      } else {
        return [...prev, featureId];
      }
    });
  };

  const handleSaveUser = async () => {
    if (!editingUser) return;

    try {
      // Get existing permissions that are not model/agent/feature
      const existingOtherPermissions = (editingUser.permissions || [])
        .filter(p => !p.startsWith('model:') && !p.startsWith('agent:') && !p.startsWith('feature:'));
      
      // Build permissions from selected resources
      const modelPermissions = selectedModelIds.map(id => `model:${id}`);
      const agentPermissions = selectedAgentIds.map(id => `agent:${id}`);
      const featurePermissions = selectedFeatureIds.map(id => `feature:${id}`);
      
      // Combine all permissions
      const allPermissions = [
        ...existingOtherPermissions,
        ...modelPermissions,
        ...agentPermissions,
        ...featurePermissions
      ];
      
      const updates: UserSettingsUpdate = {
        // V3 fields
        role: editingUser.role as any,
        permissions: allPermissions,
        business_unit: businessUnit,
        manager_id: managerId || null,
        is_admin: changeIsAdmin,
        is_active: isActive
      };

      await updateUserSettingsMutation.mutateAsync({
        userId: editingUser.id,
        settings: updates,
      });
      setDetailsDialogOpen(false);
      showSnackbar('User updated successfully', 'success');
      await fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      showSnackbar('Failed to update user', 'error');
    }
  };

  const handleSaveBulkUsers = async () => {
    if (!bulkEditingUsers.length) return;

    setBulkSaving(true);
    let successCount = 0;
    let failCount = 0;

    try {
      const modelPermissions = selectedModelIds.map(id => `model:${id}`);
      const agentPermissions = selectedAgentIds.map(id => `agent:${id}`);
      const featurePermissions = selectedFeatureIds.map(id => `feature:${id}`);

      await Promise.all(
        bulkEditingUsers.map(async (user) => {
          try {
            const existingOtherPermissions = (user.permissions || [])
              .filter(p => !p.startsWith('model:') && !p.startsWith('agent:') && !p.startsWith('feature:'));

            const updates: UserSettingsUpdate = {
              role: user.role as any,
              permissions: [
                ...existingOtherPermissions,
                ...modelPermissions,
                ...agentPermissions,
                ...featurePermissions
              ],
              business_unit: user.business_unit || 'Unassigned',
              manager_id: user.manager_id || null,
              is_admin: changeIsAdmin,
              is_active: isActive
            };

            await updateUserSettingsMutation.mutateAsync({
              userId: user.id,
              settings: updates
            });
            successCount += 1;
          } catch (error) {
            console.error(`Failed to update user ${user.id}:`, error);
            failCount += 1;
          }
        })
      );

      setBulkDialogOpen(false);
      setBulkEditingUsers([]);

      if (failCount === 0) {
        showSnackbar(`Updated ${successCount} users successfully`, 'success');
      } else if (successCount > 0) {
        showSnackbar(`Updated ${successCount} users, ${failCount} failed`, 'error');
      } else {
        showSnackbar('Failed to update selected users', 'error');
      }

      await fetchUsers();
    } finally {
      setBulkSaving(false);
    }
  };

  const handleDeleteUser = async (user: UserSettings) => {
    try {
      await deleteUserSettingsMutation.mutateAsync(user.id);
      showSnackbar('User deleted successfully', 'success');
      await fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      showSnackbar(error instanceof Error ? error.message : 'Failed to delete user', 'error');
    }
  };

  const handleBulkDeleteUsers = async (selectedUsers: UserSettings[]) => {
    if (!selectedUsers.length) {
      showSnackbar('Please select at least one user', 'error');
      return;
    }

    let successCount = 0;
    let failCount = 0;

    await Promise.all(
      selectedUsers.map(async (user) => {
        try {
          await deleteUserSettingsMutation.mutateAsync(user.id);
          successCount += 1;
        } catch (error) {
          console.error(`Failed to delete user ${user.id}:`, error);
          failCount += 1;
        }
      })
    );

    if (failCount === 0) {
      showSnackbar(`Deleted ${successCount} users successfully`, 'success');
    } else if (successCount > 0) {
      showSnackbar(`Deleted ${successCount} users, ${failCount} failed`, 'error');
    } else {
      showSnackbar('Failed to delete selected users', 'error');
    }

    await fetchUsers();
  };
  
  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  return (
    <Box sx={{ width: '100%', height: '100%', overflow: 'auto' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="admin tabs"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            {visibleTabs.map((tab, index) => (
              <Tab
                key={tab.label}
                label={tab.label === 'Permission Requests' ? (
                  <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                    <span>{tab.label}</span>
                    {canReviewPermissionRequests ? <PendingRequestsBadge count={pendingPermissionRequestsCount} /> : null}
                  </Box>
                ) : tab.label}
                {...a11yProps(index)}
              />
            ))}
          </Tabs>
        </Box>
        
        {/* Dashboard - always visible */}
        <TabPanel value={tabValue} index={visibleTabs.findIndex(t => t.label === 'Dashboard')}>
          <DashboardTab />
        </TabPanel>

        {/* Users - always visible */}
        <TabPanel value={tabValue} index={visibleTabs.findIndex(t => t.label === 'Users')}>
          {showUsersStaleWarning ? (
            <Box sx={{ mb: 2 }}>
              <QueryStaleDataWarning
                message="Showing last synced users. We couldn't refresh the latest list."
                onRetry={() => {
                  void fetchUsers();
                }}
              />
            </Box>
          ) : null}

          {blockingUsersError ? (
            <QueryErrorState
              title="Unable to load users"
              message={blockingUsersError}
              onRetry={() => {
                void fetchUsers();
              }}
              minHeight={260}
            />
          ) : (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0}>
              <Box />
              <Box>
                <Tooltip title="Refresh">
                  <span>
                    <IconButton aria-label="refresh users" color="primary" onClick={fetchUsers} sx={{ mr: 1 }}>
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>

            <UsersTab
              users={users}
              loading={loading}
              onAction={handleActionClick}
              onBulkEdit={handleBulkEditClick}
              onBulkDelete={handleBulkDeleteUsers}
              onDeleteUser={handleDeleteUser}
            />
          </>
          )}
        </TabPanel>
        
        {/* Permission Requests - always visible */}
        <TabPanel value={tabValue} index={visibleTabs.findIndex(t => t.label === 'Permission Requests')}>
          <PermissionRequestsTab isAdmin={isAdmin} isManager={userRole === 'manager'} />
        </TabPanel>

        {/* Admin-only tabs - only render if admin */}
        {isAdmin && (
          <>
            <TabPanel value={tabValue} index={visibleTabs.findIndex(t => t.label === 'Models')}>
              <ModelsTab />
            </TabPanel>

            <TabPanel value={tabValue} index={visibleTabs.findIndex(t => t.label === 'Agents')}>
              <AgentsTab />
            </TabPanel>

            <TabPanel value={tabValue} index={visibleTabs.findIndex(t => t.label === 'Features')}>
              <FeaturesTab />
            </TabPanel>

            <TabPanel value={tabValue} index={visibleTabs.findIndex(t => t.label === 'System Settings')}>
              <SystemSettingsTab />
            </TabPanel>
          </>
        )}

      {/* Bulk Edit Users Dialog */}
      <DialogWrapper open={bulkDialogOpen} onClose={() => setBulkDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit {bulkEditingUsers.length} Selected Users
          <IconButton
            aria-label="close"
            onClick={() => setBulkDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              These values will be applied to all selected users.
            </Typography>

            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 0, mt: 1 }}>
              Model Permissions
            </Typography>

            {modelsLoading ? (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Box sx={{ mb: 2 }}>
                <FormGroup>
                  {availableModels.map(model => (
                    <FormControlLabel
                      key={model.id}
                      control={
                        <Checkbox
                          checked={model.is_implicit || selectedModelIds.includes(model.id)}
                          onChange={() => handleModelPermissionToggle(model.id)}
                          disabled={model.is_implicit}
                        />
                      }
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          <span>{model.name}</span>
                          {model.is_implicit && (
                            <Chip
                              label="Implicit (All Users)"
                              size="small"
                              color="default"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                          {!model.is_active && (
                            <Chip
                              label="Inactive"
                              size="small"
                              color="warning"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      }
                      sx={{
                        opacity: model.is_implicit ? 0.6 : 1,
                        '& .MuiFormControlLabel-label': { fontSize: '0.9rem' },
                        my: -0.9
                      }}
                    />
                  ))}
                </FormGroup>

                {availableModels.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No chat models available. Please configure models in the Models tab.
                  </Typography>
                )}
              </Box>
            )}

            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 0 }}>
              Agent Permissions
            </Typography>

            {agentsLoading ? (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Box sx={{ mb: 2 }}>
                <FormGroup>
                  {availableAgents.map(agent => (
                    <FormControlLabel
                      key={agent.id}
                      control={
                        <Checkbox
                          checked={agent.is_implicit || selectedAgentIds.includes(agent.id)}
                          onChange={() => handleAgentPermissionToggle(agent.id)}
                          disabled={agent.is_implicit}
                        />
                      }
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          <span>{agent.name}</span>
                          {agent.is_implicit && (
                            <Chip
                              label="Implicit (All Users)"
                              size="small"
                              color="default"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                          {!agent.is_active && (
                            <Chip
                              label="Inactive"
                              size="small"
                              color="warning"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      }
                      sx={{
                        opacity: agent.is_implicit ? 0.6 : 1,
                        '& .MuiFormControlLabel-label': { fontSize: '0.9rem' },
                        my: -0.9
                      }}
                    />
                  ))}
                </FormGroup>

                {availableAgents.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No agents available. Please configure agents in the Agents tab.
                  </Typography>
                )}
              </Box>
            )}

            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 0 }}>
              Feature Permissions
            </Typography>

            {featuresLoading ? (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Box sx={{ mb: 2 }}>
                <FormGroup>
                  {availableFeatures.map(feature => (
                    <FormControlLabel
                      key={feature.id}
                      control={
                        <Checkbox
                          checked={feature.is_implicit || selectedFeatureIds.includes(feature.id)}
                          onChange={() => handleFeaturePermissionToggle(feature.id)}
                          disabled={feature.is_implicit}
                        />
                      }
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          <span>{feature.name}</span>
                          {feature.is_implicit && (
                            <Chip
                              label="Implicit (All Users)"
                              size="small"
                              color="default"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                          {!feature.is_active && (
                            <Chip
                              label="Inactive"
                              size="small"
                              color="warning"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      }
                      sx={{
                        opacity: feature.is_implicit ? 0.6 : 1,
                        '& .MuiFormControlLabel-label': { fontSize: '0.9rem' },
                        my: -0.9
                      }}
                    />
                  ))}
                </FormGroup>

                {availableFeatures.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No features available. Please configure features in the Features tab.
                  </Typography>
                )}
              </Box>
            )}

            {isAdmin && (
              <FormControlLabel
                control={
                  <Switch
                    checked={changeIsAdmin}
                    onChange={(e) => setChangeIsAdmin(e.target.checked)}
                    color="primary"
                  />
                }
                label="Is Admin"
                sx={{ mt: 0, mb: 0 }}
              />
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  color="primary"
                />
              }
              label="Account Active"
              sx={{ mt: 0, mb: 0 }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-end' }}>
          <Box sx={{ pr: 2 }}>
            <Button onClick={() => setBulkDialogOpen(false)} color="primary" variant="contained" sx={{ mr: 1 }}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveBulkUsers}
              color="primary"
              variant="contained"
              disabled={bulkSaving || updateUserSettingsMutation.isPending}
            >
              {bulkSaving ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </DialogActions>
      </DialogWrapper>

      {/* Edit User Dialog */}
      <DialogWrapper open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editMode ? "Edit User: " : "View User: "} {editingUser?.name || editingUser?.id}
          <IconButton
            aria-label="close"
            onClick={() => setDetailsDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Compact summary row: read-only Email and Last Login */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 0 }}>
              <TextField
                label="Email"
                value={editingUser?.email || ''}
                fullWidth
                margin="normal"
                variant="outlined"
                slotProps={{ 
                  input:{
                    readOnly: true }}
                }
              />
              <TextField
                label="Last Login"
                value={editingUser?.last_login ? new Date(editingUser.last_login).toLocaleString() : '—'}
                fullWidth
                margin="normal"
                variant="outlined"
                slotProps={{ 
                  input:{
                    readOnly: true }}
                }
              />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 0 }}>
              {/*<TextField
                select
                label="Role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                fullWidth
                margin="normal"
                variant="outlined"
                slotProps={editMode ? {
                    select: {
                      native: true,
                      inputProps: {
                        'aria-label': 'Role',
                        title: 'Role'
                      }
                    }}
                 :
                    {input:{
                      readOnly: true }
                }}
              >
                <option value="user">User</option>
                <option value="manager">Manager</option>
                <option value="admin">Admin</option>
              </TextField>*/}
              <TextField
                label="Role"
                value={editingUser?.role || 'Not assigned'}
                fullWidth
                margin="normal"
                variant="outlined"
                slotProps={{ 
                  input:{
                    readOnly: true }}
                }
              />

              <TextField
                label="Manager"
                value={managerName || 'Not assigned'}
                fullWidth
                margin="normal"
                variant="outlined"
                slotProps={{ 
                  input:{
                    readOnly: true }}
                }
              />
            </Box>
            
            <TextField
              label="Business Unit"
              value={businessUnit || 'Unassigned'}
              fullWidth
              margin="normal"
              variant="outlined"
              slotProps={{ 
                  input:{
                    readOnly: true }}
                }
              // helperText="Synced from Entra ID on login"
            />
            
            {/* Model Permissions Section */}
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 0, mt: 1 }}>
              Model Permissions
            </Typography>
            
            {modelsLoading ? (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Box sx={{ mb: 2 }}>
                <FormGroup>
                  {availableModels
                    .map(model => (
                      <FormControlLabel
                        key={model.id}
                        control={
                          <Checkbox
                            checked={model.is_implicit || selectedModelIds.includes(model.id)}
                            onChange={() => handleModelPermissionToggle(model.id)}
                            disabled={model.is_implicit}
                            readOnly={!editMode}
                          />
                        }
                        label={
                          <Box display="flex" alignItems="center" gap={1}>
                            <span>{model.name}</span>
                            {model.is_implicit && (
                              <Chip 
                                label="Implicit (All Users)" 
                                size="small" 
                                color="default"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                            {!model.is_active && (
                              <Chip 
                                label="Inactive" 
                                size="small" 
                                color="warning"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            )}
                          </Box>
                        }
                        sx={{ 
                          opacity: model.is_implicit ? 0.6 : 1,
                          '& .MuiFormControlLabel-label': { fontSize: '0.9rem' },
                          my: -0.9
                        }}
                      />
                    ))}
                </FormGroup>
                
                {availableModels.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No chat models available. Please configure models in the Models tab.
                  </Typography>
                )}
              </Box>
            )}
            
            {/* Agent Permissions Section */}
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 0 }}>
              Agent Permissions
            </Typography>
            
            {agentsLoading ? (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Box sx={{ mb: 2 }}>
                <FormGroup>
                  {availableAgents.map(agent => (
                    <FormControlLabel
                      key={agent.id}
                      control={
                        <Checkbox
                          checked={agent.is_implicit || selectedAgentIds.includes(agent.id)}
                          onChange={() => handleAgentPermissionToggle(agent.id)}
                          disabled={agent.is_implicit}
                          readOnly={!editMode}
                        />
                      }
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          <span>{agent.name}</span>
                          {agent.is_implicit && (
                            <Chip 
                              label="Implicit (All Users)" 
                              size="small" 
                              color="default"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                          {!agent.is_active && (
                            <Chip 
                              label="Inactive" 
                              size="small" 
                              color="warning"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      }
                      sx={{ 
                        opacity: agent.is_implicit ? 0.6 : 1,
                        '& .MuiFormControlLabel-label': { fontSize: '0.9rem' },
                        my: -0.9
                      }}
                    />
                  ))}
                </FormGroup>
                
                {availableAgents.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No agents available. Please configure agents in the Agents tab.
                  </Typography>
                )}
              </Box>
            )}
            
            {/* Feature Permissions Section */}
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, mb: 0 }}>
              Feature Permissions
            </Typography>
            
            {featuresLoading ? (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Box sx={{ mb: 2 }}>
                <FormGroup>
                  {availableFeatures.map(feature => (
                    <FormControlLabel
                      key={feature.id}
                      control={
                        <Checkbox
                          checked={feature.is_implicit || selectedFeatureIds.includes(feature.id)}
                          onChange={() => handleFeaturePermissionToggle(feature.id)}
                          disabled={feature.is_implicit}
                          readOnly={!editMode}
                        />
                      }
                      label={
                        <Box display="flex" alignItems="center" gap={1}>
                          <span>{feature.name}</span>
                          {feature.is_implicit && (
                            <Chip 
                              label="Implicit (All Users)" 
                              size="small" 
                              color="default"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                          {!feature.is_active && (
                            <Chip 
                              label="Inactive" 
                              size="small" 
                              color="warning"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      }
                      sx={{ 
                        opacity: feature.is_implicit ? 0.6 : 1,
                        '& .MuiFormControlLabel-label': { fontSize: '0.9rem' },
                        my: -0.9
                      }}
                    />
                  ))}
                </FormGroup>
                
                {availableFeatures.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No features available. Please configure features in the Features tab.
                  </Typography>
                )}
              </Box>
            )}
            {isAdmin && (
            <Tooltip title={editingUser?.id === currentUserId ? "You cannot modify your own admin status" : ""} placement="top">
              <span>
            <FormControlLabel
              control={
                <Switch 
                  readOnly={!editMode || editingUser?.id === currentUserId}
                  checked={changeIsAdmin}
                  onChange={(e) => editingUser?.id !== currentUserId && setChangeIsAdmin(e.target.checked)} 
                  color="primary"
                  disabled={editingUser?.id === currentUserId}
                />
              }
              label="Is Admin"
              sx={{ mt: 0, mb: 0 }}
            />
              </span>
            </Tooltip>)}
            <Tooltip title={editingUser?.id === currentUserId ? "You cannot modify your own account status" : ""} placement="top">
              <span>
            <FormControlLabel
              control={
                <Switch 
                  readOnly={!editMode || editingUser?.id === currentUserId}
                  checked={isActive} 
                  onChange={(e) => editingUser?.id !== currentUserId && setIsActive(e.target.checked)} 
                  color="primary"
                  disabled={editingUser?.id === currentUserId}
                />
              }
              label="Account Active"
              sx={{ mt: 0, mb: 0 }}
            />
              </span>
            </Tooltip>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-end' }}>
          <Box sx={{ pr: 2 }}>
            <Button onClick={() => setDetailsDialogOpen(false)} color="primary" variant="contained" sx={{ mr: 1 }}>{editMode ? "Cancel" : "Close"}</Button>
            {editMode && (
            <Button 
              onClick={handleSaveUser} 
              color="primary" 
              variant="contained"
              disabled={!editMode}
            >
              Save
            </Button>)}
          </Box>
        </DialogActions>
        </DialogWrapper>

      {/* New user creation UI removed from Admin Panel */}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminPanel;
